import { useCallback, useEffect, useState } from "react";

import { UnauthorizedException } from "@eisox/api";
import type { Session, SignInResponseBody } from "@eisox/api/core-api";
import { captureException, setSentryUser } from "@eisox/tools";
import { createContext } from "@radix-ui/react-context";
import type { QueryClient } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";

import type { AuthRepository, AuthState, SignInCredentials, TokenStorage } from "./auth.type";
import { AuthLoadingScreen } from "./components/auth-loading-screen.component";
import { authTokenStorage, onSessionRevoked } from "./utils";

const NAME = "AuthContext";

const [AuthContextProvider, useAuthContext] = createContext<AuthState>(NAME);

interface AuthProviderProps {
  children: React.ReactNode;
  authApi: AuthRepository;
  authorize?: (session: Session) => void | Promise<void>;
  storage?: TokenStorage;
  onSignedOut: () => void | Promise<void>;
  fallback?: React.ReactNode;
}

type AuthSnapshot =
  | { status: "loading" }
  | { status: "anonymous" }
  | { status: "authenticated"; user: Session; token: string };

async function clearQueryCacheAfterUnmount(queryClient: QueryClient): Promise<void> {
  await new Promise<void>(resolve => {
    setTimeout(() => {
      queryClient.clear();
      resolve();
    }, 0);
  });
}

const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  authApi,
  authorize,
  storage = authTokenStorage,
  onSignedOut,
  fallback = <AuthLoadingScreen />,
}) => {
  const queryClient = useQueryClient();
  const [snapshot, setSnapshot] = useState<AuthSnapshot>(() =>
    storage.get() === null ? { status: "anonymous" } : { status: "loading" },
  );

  const isAuthenticated = snapshot.status === "authenticated";
  const user = isAuthenticated ? snapshot.user : null;
  const token = isAuthenticated ? snapshot.token : null;

  const authorizeAndCommit = async (nextToken: string, session: Session) => {
    storage.set(nextToken);
    try {
      await authorize?.(session);
    } catch (error) {
      storage.clear();
      throw error;
    }
    setSnapshot({ status: "authenticated", user: session, token: nextToken });
  };

  // Apps set QueryClient staleTime to Infinity; without 0, refresh would return the cached user.
  const fetchSession = (staleTime?: number) => queryClient.fetchQuery({ ...authApi.queries.session(), staleTime });

  const signIn = async (credentials: SignInCredentials) => {
    const response = await authApi.signIn(credentials);
    await authorizeAndCommit(response.token, response.user);
    return response.user;
  };

  const adoptSession = async (response: SignInResponseBody) => {
    await authorizeAndCommit(response.token, response.user);
  };

  const refreshSession = async () => {
    if (snapshot.status !== "authenticated") return;
    try {
      const session = await fetchSession(0);
      setSnapshot({ status: "authenticated", user: session, token: snapshot.token });
    } catch (error) {
      if (!(error instanceof UnauthorizedException)) return;
      storage.clear();
      setSnapshot({ status: "anonymous" });
      await clearQueryCacheAfterUnmount(queryClient);
    }
  };

  const signOut = useCallback(async () => {
    try {
      await authApi.signOut();
    } catch (error) {
      if (!(error instanceof UnauthorizedException)) captureException(error);
    }

    setSnapshot({ status: "anonymous" });
    storage.clear();
    await onSignedOut();
    await clearQueryCacheAfterUnmount(queryClient);
  }, [authApi, onSignedOut, queryClient, storage]);

  useEffect(() => {
    if (snapshot.status !== "loading") return;

    const stored = storage.get();

    if (stored === null) {
      setSnapshot({ status: "anonymous" });
      return;
    }

    const boot = { cancelled: false };
    void (async () => {
      try {
        const session = await fetchSession();
        if (!boot.cancelled) await authorizeAndCommit(stored, session);
      } catch (error) {
        if (error instanceof UnauthorizedException) {
          storage.clear();
        }
        if (!boot.cancelled) {
          setSnapshot({ status: "anonymous" });
          if (error instanceof UnauthorizedException) {
            await clearQueryCacheAfterUnmount(queryClient);
          }
        }
      }
    })();

    return () => {
      boot.cancelled = true;
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot.status]);

  useEffect(() => {
    if (snapshot.status !== "authenticated") return;

    // One revocation per subscription; signOut()'s own 401 must not re-enter.
    let signedOut = false;

    return onSessionRevoked(() => {
      if (signedOut) return;
      signedOut = true;
      void signOut();
    });
  }, [snapshot.status, signOut]);

  useEffect(() => {
    setSentryUser(snapshot.status === "authenticated" ? { id: snapshot.user.id } : null);
  }, [snapshot]);

  if (snapshot.status === "loading") return fallback;

  return (
    <AuthContextProvider
      isAuthenticated={isAuthenticated}
      user={user}
      token={token}
      signIn={signIn}
      adoptSession={adoptSession}
      refreshSession={refreshSession}
      signOut={signOut}
    >
      {children}
    </AuthContextProvider>
  );
};

export { AuthProvider, useAuthContext };
export type { AuthProviderProps };
